import app from 'Cloud/Application/app';
import debounce from 'lodash.debounce';
import { stringify } from 'qs';
import { goTo, isGalleryStorage } from 'reactApp/appHelpers/appHelpers';
import { IS_REACT_PAGE } from 'reactApp/appHelpers/configHelpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { getFeatureAbMailAttachesAfterClose } from 'reactApp/modules/features/features.selectors';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage, getParams } from 'reactApp/modules/router/router.selectors';
import { getSearchQueryString } from 'reactApp/modules/search/search.helpers';
import { setFromAttaches } from 'reactApp/modules/settings/settings.module';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getCurrentFolder } from 'reactApp/modules/storage/storage.selectors';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { store } from 'reactApp/store';
import { getEncodePath, getHomeFolderPath, getPathName } from 'reactApp/utils/urlHelper';
import { call, put, select } from 'redux-saga/effects';

const getPathForHistory = (path?: string, storage?: EStorageType) => {
    if (path) {
        const encodedPath = getEncodePath(path);
        storage = storage === EStorageType.viewerAttaches ? EStorageType.attaches : storage;
        return storage === EStorageType.home
            ? getHomeFolderPath(encodedPath)
            : `/${storage}${storage === EStorageType.attaches ? '/' : ''}${encodedPath}`;
    }

    return getPathName();
};

function* pushSearchParams(path: string) {
    const { query, searchType } = yield select(SettingsSelectors.getQueryParams);
    const id = getPathForHistory(path, EStorageType.search);
    const search = getSearchQueryString(query, searchType);

    yield put(historyPush({ id, search }));
}

export function* pushWeblink({
    path,
    weblink,
    storage = EStorageType.home,
}: { path?: string; weblink?: string; storage?: EStorageType } = {}) {
    const query = weblink ? `?weblink=${weblink}` : '';

    if (storage === EStorageType.documents) {
        return;
    }
    yield put(historyPush({ id: `${getPathForHistory(path, storage)}${query}` }));
}

const debouncedGoTo = debounce(
    (id, storage, params, fromLocation = true) => goTo({ id, storage, params }, false, fromLocation, false, false, true),
    250
);

const STORAGE_MAP = {
    [EStorageType.viewerAttaches]: EStorageType.attaches,
};

const mapStorage = (storage: EStorageType): EStorageType => STORAGE_MAP[storage] || storage;

export function* navigateToItem(item: CloudItem | null | undefined) {
    if (!item) {
        return;
    }
    const routerParams: ReturnType<typeof getParams> = yield select(getParams) || {};
    const currentStorage: ReturnType<typeof getCurrentStorage> = yield select(getCurrentStorage);
    const isItemHome = item.storage === EStorageType.home;
    const weblink = 'weblink' in item && item.weblink;
    // TypeError: Cannot add property weblink, object is not extensible
    // Селектор возвращает не простой объект, а защищенный от модификации, поэтому делаем его копию
    const params = { ...routerParams };
    if (isItemHome && weblink) {
        params.weblink = weblink;
    } else {
        delete params.weblink;
    }

    if (
        currentStorage === EStorageType.start ||
        item.storage === EStorageType.story ||
        (currentStorage === EStorageType.home && item.storage !== currentStorage)
    ) {
        // TODO: пока пропускаем это для виджетов
        return;
    }

    if (IS_REACT_PAGE) {
        const storage: ReturnType<typeof getCurrentStorage> = yield select(getCurrentStorage);
        const { isHome, isAlbums, isSearch } = getStorage(storage);
        const { id, parent } = item;
        const itemPath = ('home' in item && item?.home) || id;

        if (isSearch) {
            return yield call(pushSearchParams, itemPath);
        }

        yield call(pushWeblink, {
            path: isAlbums ? `/${parent}/${id}` : itemPath,
            weblink: isHome ? params.weblink : '',
            storage: item.storage,
        });
    } else {
        debouncedGoTo(item.id, mapStorage(item.storage), params);
    }
}

export const gotoAfterClose = () => {
    const state = store.getState();
    const currentFolder = getCurrentFolder(state) || app?.currentFolder?.();
    const storage = getCurrentStorage(state);
    const { isHome, isDocuments, isAlbums, isSearch, isAttaches } = getStorage(storage);
    const { query = '', searchType = '', fromCloud = false, folderId, type } = SettingsSelectors.getQueryParams(state);
    // tempexp_14505-next-line
    const abMailAttachesAfterClose = getFeatureAbMailAttachesAfterClose(state);

    if (!currentFolder) {
        return;
    }

    if (IS_REACT_PAGE) {
        // TODO: routing
        if (isHome) {
            store.dispatch(historyPush({ id: getEncodePath(getHomeFolderPath(currentFolder.id)) }));
        } else if (isDocuments) {
            store.dispatch(historyPush({ id: `${storage}/${currentFolder.name}` }));
        } else if (isAlbums) {
            store.dispatch(historyPush({ id: `${storage}/${currentFolder.id}` }));
        } else if (isSearch) {
            const search = getSearchQueryString(query, searchType);
            store.dispatch(historyPush({ id: `${storage}/`, search }));
            // tempexp_14505-start
        } else if (isAttaches && !fromCloud && abMailAttachesAfterClose === 'b') {
            store.dispatch(setFromAttaches(true));
            store.dispatch(historyPush({ id: ROOT_FOLDER_ID }));
            // tempexp_14505-end
        } else if (isAttaches) {
            const search = stringify({ fromCloud, folderId, type }, { addQueryPrefix: true, skipNulls: true });
            store.dispatch(historyPush({ id: `${storage}/`, search }));
        } else {
            store.dispatch(historyPush({ id: storage }));
        }
        return;
    }

    if (app.isSearch()) {
        app.goTo({
            id: currentFolder.id,
            params: app.getState().params,
            storage: 'search',
        });
    } else if (app.isFavorites()) {
        app.goTo({
            id: '/',
            storage: 'favorites',
        });
    } else if (app.isAttaches()) {
        app.goTo({
            id: '/',
            params: app.getState().params,
            storage: 'attaches',
        });
    } else if (!isGalleryStorage()) {
        debouncedGoTo(currentFolder.id, null, null, false);
    }
};
